import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { FiX } from 'react-icons/fi'

type SideSliderProps = {
  isOpen: boolean
  onClose: () => void
  isRTL?: boolean
  title: ReactNode
  subtitle?: ReactNode
  headerExtra?: ReactNode
  children: ReactNode
  widthClass?: string
  headerClassName?: string
  contentClassName?: string
  /** Keep children mounted while closed so form refs and live queries stay alive. */
  keepMounted?: boolean
}

/**
 * Full-height side drawer rendered on document.body so it is never clipped
 * by layout overflow and always sits above the app header (z-100).
 */
export default function SideSlider({
  isOpen,
  onClose,
  isRTL = false,
  title,
  subtitle,
  headerExtra,
  children,
  widthClass = 'max-w-2xl',
  headerClassName = 'bg-gradient-to-r from-primary-600 to-purple-600 text-white',
  contentClassName = 'p-4',
  keepMounted = false,
}: SideSliderProps) {
  useEffect(() => {
    if (!isOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKey)
    }
  }, [isOpen, onClose])

  if ((!isOpen && !keepMounted) || typeof document === 'undefined') return null

  return createPortal(
    <>
      {isOpen ? <div className="fixed inset-0 z-[220] bg-black/50" onClick={onClose} /> : null}
      <div
        className={`fixed inset-y-0 z-[230] flex h-full w-full ${widthClass} flex-col bg-white shadow-2xl ${
          isRTL ? 'left-0' : 'right-0'
        } ${isOpen ? '' : 'hidden'}`}
        dir={isRTL ? 'rtl' : 'ltr'}
        role="dialog"
        aria-modal={isOpen}
      >
        <div
          className={`flex shrink-0 items-center justify-between gap-3 px-5 py-4 ${headerClassName}`}
        >
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-xl font-bold leading-[1.8]">{title}</h2>
            {subtitle ? <div className="mt-1 text-sm text-white/90">{subtitle}</div> : null}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {headerExtra}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 hover:bg-white/20"
              aria-label={isRTL ? 'إغلاق' : 'Close'}
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className={`min-h-0 flex-1 overflow-y-auto ${contentClassName}`}>{children}</div>
      </div>
    </>,
    document.body,
  )
}
